import { Injectable, Injector } from '@angular/core';
import { BehaviorSubject, Subject, SubscriptionLike, tap } from 'rxjs';

import { WebsocketService } from '@shared/services';
import { Room, ChatEvent  } from '@shared/factories';
import { WsChatResponse   } from '@shared/models';

@Injectable({
  providedIn: 'root'
})
export class ChatEventsService {
  subscription: SubscriptionLike;

  private roomsSource            = new BehaviorSubject<Room[]>(null);
  private chatEventsSource       = new BehaviorSubject<ChatEvent>(null);
  private showActiveRoomsSource  = new BehaviorSubject<number>(null);
  private totalUnreadCountSource = new BehaviorSubject<number>(null);
  private roomOpenedSource       = new Subject<Room>();

  rooms            = this.roomsSource.asObservable();
  chatEvents       = this.chatEventsSource.asObservable();
  showActiveRooms  = this.showActiveRoomsSource.asObservable();
  totalUnreadCount = this.totalUnreadCountSource.asObservable();
  roomOpened       = this.roomOpenedSource.asObservable();

  savedRooms: Room[] = [];
  constructor( private injector: Injector ) { }

  init(): void {
    if (this.subscription) return;
    let websocketService = this.injector.get(WebsocketService);
    this.subscription = websocketService.messages.pipe(
      tap(wsEvent => {
        if (wsEvent.type !== 'ping' && wsEvent.message) {
          let kind = (wsEvent?.message as WsChatResponse)?.kind;
          if (kind === 'chat') this.chatEventsSource.next(new ChatEvent(wsEvent));
        }
      })
    ).subscribe();
  }

  destroy(): void {
    this.subscription?.unsubscribe();
    this.subscription = null;
  }

  updateRooms(rooms: Room[]): void {
    rooms.forEach(room => {
      let roomIndex = this.savedRooms?.findIndex(r => r.id === room.id);
      let savedRoom = roomIndex >= 0 && this.savedRooms[roomIndex];
      if (savedRoom) {
        let newRoom = new Room({
          ...savedRoom.toJSON(),
          ...room.toJSON(),
          ...{
            openedAt: savedRoom.openedAt !== null && savedRoom.openedAt !== undefined ? savedRoom.openedAt.toISOString() : null,
            active:   savedRoom.active   !== null && savedRoom.active   !== undefined ? savedRoom.active                 : null,
            recent:   savedRoom.recent   !== null && savedRoom.recent   !== undefined ? savedRoom.recent                 : null,
          }
        });
        this.savedRooms.splice(roomIndex, 1, newRoom);
      } else this.savedRooms.push(room);
    });

    this.roomsSource.next(this.savedRooms);
  }

  get sortedRooms(): Room[] {
    return this.savedRooms?.filter(r => r.openedAt)?.sort((a,b) => b.openedAt?.getTime() - a.openedAt?.getTime());
  }

  changeActiveRoomsNumber(limit: number): void {
    this.showActiveRoomsSource.next(limit);
    this.updateActiveRooms();
  }

  changeTotalUnreadCount(count: number): void {
    this.totalUnreadCountSource.next(count);
  }

  private updateActiveRooms(): void {
    this.savedRooms?.forEach(r => r.active = false);
    this.sortedRooms.forEach((r, index) => { if (index+1 <= this.showActiveRoomsSource.value) r.active = true; });
    this.updateRooms(this.savedRooms);
  }

  openRoom(room: Room): void {
    let roomIndex = this.savedRooms?.findIndex(r => r?.id === room.id);
    if (roomIndex >= 0) {
      this.savedRooms[roomIndex].openedAt = new Date();
      this.savedRooms[roomIndex].active   = true;
      this.savedRooms[roomIndex].recent   = true;
      this.updateActiveRooms();
      this.roomOpenedSource.next(this.savedRooms[roomIndex]);
    } else {
      this.savedRooms.push(room);
      this.updateActiveRooms();
      this.openRoom(room);
    }
  }

  closeRoom(room: Room, removeRecent: boolean = null): void {
    let roomIndex = this.savedRooms?.findIndex(r => r?.id === room.id);
    if (roomIndex >= 0) {
      this.savedRooms[roomIndex].openedAt = null;
      this.savedRooms[roomIndex].active   = null;
      if (removeRecent) this.savedRooms[roomIndex].recent = null;
      this.updateRooms([this.savedRooms[roomIndex]]);
    }
  }

  markAllAsReadCallback(roomId: number): void {
    let roomIndex = this.savedRooms?.findIndex(r => r?.id === roomId);
    if (roomIndex >= 0) {
      this.savedRooms[roomIndex].messages.filter(m => !m.viewerIds || !m.viewerIds.length).forEach(m => {
        if (!m.viewerIds) m.viewerIds = [];
        m.viewerIds.push(this.savedRooms[roomIndex].meAsPartisipant.id);
      });
      this.savedRooms[roomIndex].meAsPartisipant.unreadMessagesCount = null;
      this.updateRooms([this.savedRooms[roomIndex]]);
    }
  }

}
